import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { activitiesAPI } from '../services/api';
import { motion } from 'framer-motion';
import UserAvatar from '../components/UI/UserAvatar';
import {
  FunnelIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  DocumentArrowDownIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

/* ── Action config ── */
const ACTION_CONFIG = {
  create:        { icon: PlusCircleIcon,            bg: 'bg-emerald-500', label: 'Created' },
  update:        { icon: PencilSquareIcon,           bg: 'bg-blue-500',    label: 'Updated' },
  delete:        { icon: TrashIcon,                  bg: 'bg-red-500',     label: 'Deleted' },
  login:         { icon: ArrowRightOnRectangleIcon,  bg: 'bg-indigo-500',  label: 'Logged in' },
  logout:        { icon: ArrowLeftOnRectangleIcon,   bg: 'bg-gray-500',    label: 'Logged out' },
  status_change: { icon: ArrowsRightLeftIcon,        bg: 'bg-amber-500',   label: 'Status changed' },
  assignment:    { icon: UserGroupIcon,              bg: 'bg-purple-500',  label: 'Assigned' },
  export:        { icon: ArrowUpTrayIcon,            bg: 'bg-cyan-500',    label: 'Exported' },
  import:        { icon: ArrowDownTrayIcon,          bg: 'bg-cyan-500',    label: 'Imported' },
  email_sent:    { icon: EnvelopeIcon,               bg: 'bg-pink-500',    label: 'Email sent' },
  pdf_generated: { icon: DocumentArrowDownIcon,      bg: 'bg-orange-500',  label: 'PDF generated' },
  note_added:    { icon: DocumentTextIcon,           bg: 'bg-teal-500',    label: 'Note added' },
};

const ENTITY_CONFIG = {
  user:         { label: 'User',         badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' },
  lead:         { label: 'Lead',         badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  deal:         { label: 'Deal',         badge: 'bg-purple-50 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
  quote:        { label: 'Quote',        badge: 'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  product:      { label: 'Product',      badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  organization: { label: 'Organization', badge: 'bg-teal-50 text-teal-700 dark:bg-teal-900 dark:text-teal-300' },
  settings:     { label: 'Settings',     badge: 'bg-gray-50 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

/* ── Helpers ── */
function relativeTime(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr  = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function dateGroupLabel(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const activityDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today - activityDate) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/* ── Component ── */
const Activity = () => {
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['activities', actionFilter, entityFilter, page],
    queryFn: async () => {
      const params = { page, limit: 30 };
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity = entityFilter;
      const response = await activitiesAPI.getAll(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const activitiesList = useMemo(() => data?.data || [], [data]);
  const pagination = data?.pagination;

  // Group activities by date
  const grouped = useMemo(() => {
    const groups = [];
    let currentLabel = null;
    activitiesList.forEach((a) => {
      const label = dateGroupLabel(a.timestamp);
      if (label !== currentLabel) {
        currentLabel = label;
        groups.push({ label, items: [] });
      }
      groups[groups.length - 1].items.push(a);
    });
    return groups;
  }, [activitiesList]);

  /* ── Stats counters ── */
  const todayCount = activitiesList.filter((a) => {
    const d = new Date(a.timestamp);
    const n = new Date();
    return d.toDateString() === n.toDateString();
  }).length;

  /* ── Loading skeleton ── */
  if (isLoading && !data) {
    return (
      <div className="space-y-5">
        <div>
          <div className="h-7 w-40 bg-gray-200 dark:bg-surface-700 rounded-lg animate-pulse" />
          <div className="h-4 w-64 bg-gray-100 dark:bg-surface-700 rounded mt-2 animate-pulse" />
        </div>
        <div className="bg-white dark:bg-surface-800 shadow-sm rounded-xl border border-gray-200 dark:border-surface-700 p-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-start gap-4 py-4">
              <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-surface-700 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-surface-700 rounded w-3/4 animate-pulse" />
                <div className="h-3 bg-gray-100 dark:bg-surface-700 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } };
  const itemVariants = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-5">
      {/* ── Header ── */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Activity Log</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Track all actions across your workspace
          </p>
        </div>
        {pagination && (
          <div className="hidden sm:flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-lg text-xs font-medium">
              <ClockIcon className="w-3.5 h-3.5" />
              {todayCount} today
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {pagination.total} total activities
            </span>
          </div>
        )}
      </motion.div>

      {/* ── Filters ── */}
      <motion.div variants={itemVariants} className="card-premium rounded-2xl p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
            >
              <option value="">All Actions</option>
              {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <select
              value={entityFilter}
              onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-surface-600 rounded-xl bg-white dark:bg-surface-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
            >
              <option value="">All Entities</option>
              {Object.entries(ENTITY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          {(actionFilter || entityFilter) && (
            <button
              onClick={() => { setActionFilter(''); setEntityFilter(''); setPage(1); }}
              className="btn btn-ghost text-xs px-3 py-2"
            >
              Clear filters
            </button>
          )}
        </div>
      </motion.div>

      {/* ── Timeline ── */}
      <motion.div variants={itemVariants} className="card-premium rounded-2xl overflow-hidden">
        {activitiesList.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {grouped.map((group) => (
              <div key={group.label}>
                {/* Date header */}
                <div className="sticky top-0 z-10 px-5 py-2 bg-gray-50 dark:bg-surface-900 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {group.label}
                  </span>
                </div>

                {/* Activity items */}
                <ul>
                  {group.items.map((activity, idx) => {
                    const actionCfg = ACTION_CONFIG[activity.action] || ACTION_CONFIG.update;
                    const entityCfg = ENTITY_CONFIG[activity.entity];
                    const IconComponent = actionCfg.icon;

                    return (
                      <li
                        key={activity._id}
                        className="relative group hover:bg-gray-50 dark:hover:bg-surface-700 transition-colors duration-150"
                      >
                        <div className="px-5 py-3.5 flex items-start gap-3.5">
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${actionCfg.bg}`}>
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {activity.user?.firstName} {activity.user?.lastName}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {activity.description}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 mt-1.5">
                              {entityCfg && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-2xs font-medium ${entityCfg.badge}`}>
                                  {entityCfg.label}
                                </span>
                              )}
                              {activity.entityName && (
                                <span className="text-xs text-gray-600 dark:text-gray-300 font-medium truncate max-w-[200px]">
                                  {activity.entityName}
                                </span>
                              )}
                              {activity.ipAddress && (
                                <span className="text-2xs text-gray-400 dark:text-gray-500 hidden sm:inline">
                                  IP: {activity.ipAddress === '::1' ? 'localhost' : activity.ipAddress}
                                </span>
                              )}
                            </div>

                            {/* Changes detail */}
                            {activity.changes && Object.keys(activity.changes).length > 0 && (
                              <div className="mt-2 bg-gray-50 dark:bg-surface-900 rounded-lg p-2.5 text-xs space-y-1 border border-gray-100 dark:border-gray-700">
                                {Object.entries(activity.changes).map(([key, value]) => (
                                  <div key={key} className="flex items-center gap-1.5">
                                    <span className="font-medium text-gray-500 dark:text-gray-400 capitalize">{key}:</span>
                                    {typeof value === 'object' && value?.from !== undefined ? (
                                      <span className="text-gray-700 dark:text-gray-300">
                                        <span className="line-through text-gray-400">{String(value.from)}</span>
                                        <span className="mx-1">→</span>
                                        <span className="font-medium text-gray-900 dark:text-white">{String(value.to)}</span>
                                      </span>
                                    ) : (
                                      <span className="text-gray-700 dark:text-gray-300">{String(value)}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Timestamp + Avatar */}
                          <div className="flex-shrink-0 flex items-center gap-3">
                            <time
                              dateTime={activity.timestamp}
                              className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
                              title={new Date(activity.timestamp).toLocaleString()}
                            >
                              {relativeTime(activity.timestamp)}
                            </time>
                            <UserAvatar user={activity.user} size="sm" />
                          </div>
                        </div>

                        {/* Connector line */}
                        {idx < group.items.length - 1 && (
                          <div className="absolute left-[2.1rem] top-12 bottom-0 w-px bg-gray-100 dark:bg-gray-700" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 px-6">
            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-surface-700 flex items-center justify-center mx-auto mb-3">
              <FunnelIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No activities found</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {actionFilter || entityFilter
                ? 'Try adjusting your filters.'
                : 'Activity will appear here as you use the system.'}
            </p>
            {(actionFilter || entityFilter) && (
              <button
                onClick={() => { setActionFilter(''); setEntityFilter(''); }}
                className="btn btn-secondary text-xs mt-4 px-3 py-1.5"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* ── Pagination ── */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.pages}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page <= 1}
                className="btn btn-ghost px-2 py-1.5 text-xs disabled:opacity-40"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                let pageNum;
                if (pagination.pages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.page <= 3) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + i;
                } else {
                  pageNum = pagination.page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                      pagination.page === pageNum
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                disabled={pagination.page >= pagination.pages}
                className="btn btn-ghost px-2 py-1.5 text-xs disabled:opacity-40"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Activity;




